import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { can } from "../../auth/can.ts";
import { RootState } from "../../store/index.ts";
import { notification } from "../../utils/Notifications.tsx";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import CustomInput from "../formik/CustomInput.tsx";
import {
  checkPending,
  saveReactivateRequest,
} from "../../services/reactivateRequest.service.ts";
import { ReactivateRequest } from "../../models/ReactivateRequest.ts";
import PendingReactivate from "../colaboradores/PendingReactivate.tsx";
import { Partner } from "../../models/Partner.ts";
import { setFetch } from "../../store/slices/colaboradoresSlice.ts";
import { ReactivatePartnerSchema } from "../../validation/schemas/ReactivatePartnerSchema.ts";

const ReactivatePartner = ({
  name,
  id,
  closeFn,
}: {
  name: string;
  id: number;
  closeFn: () => void;
}) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { fetch } = useSelector((state: RootState) => state.colaboradores);
  const [pendingReactivate, setPendingReactivate] =
    useState<ReactivateRequest | null>(null);
  const [partner, setPartner] = useState<Partner | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    checkPendingApproval();
  }, []);

  const checkPendingApproval = async () => {
    try {
      setLoading(true);
      const response = can("partners.unsubscribe", user)
        ? await checkPending(id)
        : null;
      if (response) {
        setPendingReactivate(response);
        setPartner(response.partner);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const formik = useFormik({
    validationSchema: ReactivatePartnerSchema,
    initialValues: {
      reasonReactivation: "",
      reactivationDate: getTodayDate(),
    },
    onSubmit: async (values) => {
      try {
        setLoading(true);
        await saveReactivateRequest({
          partner: id,
          reasonReactivation: values.reasonReactivation,
          reactivationDate: values.reactivationDate,
        });
        notification(
          "",
          `Solicitud de reactivación enviada para ${name}`,
          "success"
        );
        dispatch(setFetch(!fetch));
        await checkPendingApproval();
      } catch (error: any) {
        console.error(error);
        notification(
          "Error :(",
          error?.response?.data?.message ||
            `Ocurrio un error al tratar de solicitar la reactivación de ${toCamelCase(
              name
            )}`,
          "danger"
        );
      } finally {
        setLoading(false);
      }
    },
  });

  if (loading && !pendingReactivate) {
    return <div>Cargando...</div>;
  }

  const handleClose = () => {
    dispatch(setFetch(!fetch));
    closeFn();
  };

  if (pendingReactivate && partner) {
    return (
      <PendingReactivate
        partner={partner}
        pendingReactivate={pendingReactivate}
        closeFn={handleClose}
      />
    );
  }

  return (
    <React.Fragment>
      <p className="fs-15px">
        ¿Estás seguro de que deseas solicitar la reactivación de{" "}
        {toCamelCase(name)}?
      </p>
      <CustomInput
        formik={formik}
        field="reasonReactivation"
        label="Razón de la reactivación"
        placeholder="Escribe la razón de la reactivación"
        sm={8}
        smLabel={4}
      />
      <CustomInput
        type="date"
        formik={formik}
        field="reactivationDate"
        label="Fecha de reactivación"
        sm={8}
        smLabel={4}
      />

      <div className="d-flex justify-content-end">
        <button
          className="btn btn-secondary me-2"
          onClick={() => handleClose()}
        >
          Cancelar
        </button>
        <button
          className="btn btn-azul"
          onClick={() => formik.handleSubmit()}
          disabled={loading}
        >
          Solicitar reactivación
        </button>
      </div>
    </React.Fragment>
  );
};

export default ReactivatePartner;
